/**
 * Admin Module Types
 *
 * UI Naming Convention:
 * - Organization fields: org* prefix (orgName, orgCity, orgCounty, etc.)
 * - User fields: user* prefix (userFirstName, userEmail, userEnabled, etc.)
 * - Role fields: role* where applicable (roleId, roleName)
 *
 * Single conversion point: UI ↔ API mapping happens in store CRUD methods.
 */

import { z } from "zod";

// =============================================================================
// ZOD SCHEMAS
// =============================================================================

/**
 * Organization schema - validates organization form data
 */
export const organizationSchema = z.object({
  orgId: z.number().optional(),
  orgName: z.string().min(2, "Organization name is required"),
  orgStreetAddress: z.string().optional(),
  orgStreetCity: z.string().min(2, "City is required"),
  orgStreetState: z.string().optional(),
  orgStreetZip: z.string().min(3, "ZIP code is required"),
  orgMailingAddress: z.string().optional(),
  orgMailingCity: z.string().optional(),
  orgMailingState: z.string().optional(),
  orgMailingZip: z.string().optional(),
  orgCounty: z.string().min(2, "County is required"),
  orgActive: z.boolean().default(true),
});

/**
 * User schema - validates user form data
 */
export const userSchema = z.object({
  userId: z.string().optional(),
  userFirstName: z.string().min(1, "First name is required"),
  userLastName: z.string().min(1, "Last name is required"),
  userDisplayName: z.string().min(1, "Display name is required"),
  userEmail: z.string().email("Valid email is required"),
  userPhoneNumber: z.string().optional(),
  userEnabled: z.boolean().default(true),
  userIsExternal: z.boolean().default(false),
});

/**
 * Role assignment schema - validates role assignment/removal interactions
 */
export const userRoleSchema = z.object({
  userRoleId: z.number().optional(),
  roleId: z.number(),
  roleName: z.string().optional(),
  organizationName: z.string().optional(),
  organizationId: z.number().optional(),
});

/**
 * Permission schema (UI representation)
 */
export const permissionSchema = z.object({
  permissionId: z.number(),
  permissionName: z.string(),
});

/**
 * Search schema - validates user search input
 */
export const userSearchSchema = z.object({
  searchQuery: z.string().min(1, "Search query is required"),
});

// =============================================================================
// INFERRED TYPES
// =============================================================================

export type OrganizationFormInput = z.infer<typeof organizationSchema>;
export type UserFormInput = z.infer<typeof userSchema>;
export type UserRoleInput = z.infer<typeof userRoleSchema>;
export type PermissionItem = z.infer<typeof permissionSchema>;
export type UserSearchInput = z.infer<typeof userSearchSchema>;

// =============================================================================
// UI LIST TYPES
// =============================================================================

/**
 * Organization summary for list views
 */
export interface OrganizationSummary {
  orgId: number;
  orgName: string;
  orgStreetCity?: string;
  orgCounty?: string;
  orgActive: boolean;
}

/**
 * User summary for list and admin tables
 */
export interface UserSummary {
  userId: string;
  userDisplayName: string;
  userEmail: string;
  userEnabled: boolean;
  userIsExternal: boolean;
}

/**
 * Role summary for selection lists
 */
export interface RoleSummary {
  roleId: number;
  roleName: string;
}

// =============================================================================
// EMPTY FORM DEFAULTS
// =============================================================================

export const emptyOrganizationForm: OrganizationFormInput = {
  orgId: undefined,
  orgName: "",
  orgStreetAddress: "",
  orgStreetCity: "",
  orgStreetState: "",
  orgStreetZip: "",
  orgMailingAddress: "",
  orgMailingCity: "",
  orgMailingState: "",
  orgMailingZip: "",
  orgCounty: "",
  orgActive: true,
};

export const emptyUserForm: UserFormInput = {
  userId: undefined,
  userFirstName: "",
  userLastName: "",
  userDisplayName: "",
  userEmail: "",
  userPhoneNumber: "",
  userEnabled: true,
  userIsExternal: false,
};

export const emptyUserRole: UserRoleInput = {
  userRoleId: undefined,
  roleId: 0,
  roleName: "",
};
